import { Button, buttonVariants } from '@plato/ui-legacy/components/ui/button';
import { cn } from '@plato/ui/util/classes';
import { MapIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { buildGoogleMapsRouteUrl } from '../../../lib/google-maps-url.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';

export type OpenRouteInMapsButtonProps = {
    day: string;
    ownerId: string;
    disabled?: boolean;
};

export function OpenRouteInMapsButton({ day, ownerId, disabled }: OpenRouteInMapsButtonProps) {
    const { t } = useTranslation();
    const sendEvent = useAnalytics();

    const { data: dayEvents } = api.platoUser.query.events.getList.useQuery(
        {
            dateFrom: day,
            dateTo: day,
            ownerId,
        },
        {
            enabled: !disabled,
        },
    );

    const mapsUrl = useMemo(() => (dayEvents ? buildGoogleMapsRouteUrl(dayEvents) : null), [dayEvents]);
    const isDisabled = disabled ?? !mapsUrl;

    const handleClick = () => {
        if (!dayEvents) {
            return;
        }

        sendEvent('route_opened_in_maps', {
            event_count: dayEvents.length,
            addresses_count: dayEvents.filter((e) => e.address).length,
            date: day,
        });
    };

    if (isDisabled || !mapsUrl) {
        return (
            <Button variant="link" icon={MapIcon} isDisabled>
                {t('open_route_in_maps', { ns: 'events' })}
            </Button>
        );
    }

    return (
        <Link
            to={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className={cn(
                buttonVariants({
                    variant: 'link',
                }),
                'p-0',
            )}
        >
            <MapIcon className="h-4 w-4" />
            {t('open_route_in_maps', { ns: 'events' })}
        </Link>
    );
}
